/**
 * Field resolution for the enhanced /fields endpoint.
 *
 * The server now handles filtering, labeling, deduplication, and custom field
 * merging. The FE maps the API response to FieldOption[].
 */

import type { FieldOption } from './opensearch-field-mapper';

// ---- Legacy Type Exports (used by programs-page.gts) ----

export interface EmployeeCustomFieldMapping {
  fieldName?: string;
  label?: string;
  sourceField?: string;
  reportDatatype?: string;
  enumValues?: string[];
}

export interface KeyLabelMapsResponse {
  companyEfKeyLabelMap?: Record<string, string>;
  tasTemplateKeyLabelMap?: Record<string, string>;
  tasTemplateKeyProvinceMap?: Record<string, string>;
}

/**
 * All valid province values returned by GET /api/v2/search/fields.
 * TEMPLATE fields are label-only UI dividers — excluded from /fields responses.
 * null is returned for non-TAS fields that have no province.
 */
export type FieldsProvince =
  'PROGRAM' | 'APPLICATION' | 'APPLICANT' | 'COURSE' | 'EMPLOYEE' | 'TEMPLATE';

/** Shape of each field in the enhanced /fields response */
export interface EnhancedSearchField {
  name: string;
  path?: string;
  resultKey?: string;
  searchPath?: string;
  filterPath?: string;
  type?: string;
  displayType?: string;
  adminOnly?: boolean;
  label?: string;
  displayLabel?: string;
  baseName?: string;
  product?: string;
  domain?: string;
  group?: string;
  section?: string;
  province?: FieldsProvince | null;
  fieldableType?: string;
  semanticRole?: string;
  nestedPath?: string;
  subfield?: boolean;
  dynamic?: boolean;
  isCustom?: boolean;
  isStandard?: boolean;
  sourceField?: string;
  mappingType?: string;
  enumDot?: string;
  enumValues?: string[];
  reportDatatype?: string;
  currencyCents?: boolean;
  visibility?: string;
  isDisplayable?: boolean;
  canonicalLeafKey?: string;
  aliases?: string[];
  capabilities?: Record<string, boolean>;
  isFilter?: boolean;
  isSortable?: boolean;
  isAggregatable?: boolean;
  displayKeys?: string[];
  isDefaultColumn?: boolean;
  shortKeys?: string[];
  queryHints?: {
    filterClause?: 'term' | 'terms' | 'match' | 'range' | 'nested';
    sortField?: string;
    collapseEligible?: boolean;
    unmappedType?: string;
    subfields?: Array<{ path: string; suffix: string; type: string }>;
    innerHitsRequired?: boolean;
    nestedSortPath?: string;
  };
}

/** Shape of the enhanced /fields response */
/** Per-dataset configuration returned by the /fields endpoint */
export interface DatasetConfig {
  companyFilterField?: string;
  defaultSort?: string[];
  defaultGroupBy?: string;
  essentialFields?: string[];
  defaultColumns?: string[];
  aggregateColumns?: string[];
}

export interface EnhancedFieldsResponse {
  modelType?: string;
  indexName?: string;
  companyId?: number;
  programTemplateId?: number;
  fieldScope?: 'standard' | 'company' | 'program_template';
  datasetConfig?: DatasetConfig;
  fields?: EnhancedSearchField[];
  shortKeyMap?: Record<string, string>;
  customFieldContainers?: Array<{
    key: string;
    label: string;
    domain: string;
    storageType: string;
    aliases?: string[];
    leafPrefix?: string;
    nestedSource?: string;
  }>;
  containerStorageTypes?: Record<string, string>;
  keyLabelMaps?: KeyLabelMapsResponse;
  employeeCustomFieldMappings?: EmployeeCustomFieldMapping[];
  availableDatasets?: Array<{ slug: string; label: string; modelType: string }>;
  groupSortOrder?: string[];
}

/**
 * Resolve fields from the enhanced /fields endpoint.
 *
 * This replaces resolveFieldsFromAllEndpoints — the server now does all
 * filtering, labeling, deduplication, and custom field merging. We only
 * need to map the response to FieldOption[].
 */
export function resolveFieldsFromEnhancedEndpoint(response: EnhancedFieldsResponse): FieldOption[] {
  const mapped = (response.fields || [])
    .filter((f) => f.isDisplayable !== false)
    .map((f): FieldOption => {
      const type = f.type || 'keyword';
      const displayType = f.displayType;
      const querySortField = f.queryHints?.sortField;
      const backendFilterable = f.isFilter ?? f.capabilities?.filter;
      const backendSortable = f.isSortable ?? f.capabilities?.sort;
      const backendAggregatable = f.isAggregatable ?? f.capabilities?.aggregate;
      const isFilterable =
        backendFilterable === false
          ? Boolean(f.queryHints?.filterClause)
          : (backendFilterable ?? true);
      const isSortable =
        backendSortable === false
          ? Boolean(querySortField)
          : (backendSortable ?? Boolean(querySortField));
      const isAggregatable =
        backendAggregatable === false
          ? Boolean(querySortField)
          : (backendAggregatable ?? Boolean(querySortField));
      const isDate = type === 'date' || displayType === 'date';
      const isNumeric =
        displayType === 'integer' ||
        displayType === 'currency' ||
        ['integer', 'long', 'float', 'double', 'short'].includes(type);

      return {
        key: f.name,
        // Prefer the API's contextualized/disambiguated displayLabel when
        // present (e.g. "Employee Email" vs the bare "Email") and fall back
        // to the canonical label, then the field name.
        label: f.displayLabel || f.label || f.name,
        type,
        group: f.group || f.section,
        province: f.province || null,
        isDate,
        isTimestamp: displayType === 'date' && type !== 'date',
        adminOnly: f.adminOnly ?? false,
        isAggregatable,
        isFilterable,
        isSortable,
        isGroupable: isAggregatable,
        isRangeQueryable: f.capabilities?.range ?? (isDate || isNumeric),
        isFullTextSearchable: f.capabilities?.fullText ?? type === 'text',
        isCustomField: f.isCustom,
        semanticRole: f.semanticRole,
        reportDatatype: f.reportDatatype || type,
        enumValues: f.enumValues,
        // Enhanced properties
        displayType,
        currencyCents: f.currencyCents,
        isDisplayable: f.isDisplayable,
        visibility: f.visibility,
        canonicalLeafKey: f.canonicalLeafKey,
        path: f.path,
        resultKey: f.resultKey,
        searchPath: f.searchPath,
        filterPath: f.filterPath,
        isDefaultColumn: f.isDefaultColumn,
        product: f.product,
        domain: f.domain,
        aliases: f.aliases,
        fieldableType: f.fieldableType,
        queryHints: f.queryHints,
        sortPath: isSortable ? querySortField || f.path || f.name : undefined,
      };
    });

  // Build a set of all keys for fast sibling lookup.
  const keySet = new Set(mapped.map((f) => f.key));

  const fields = mapped
    .filter((f) => {
      // Drop _keyword alias fields when a non-suffixed sibling exists.
      // e.g. company_id_keyword is redundant when company_id is present.
      if (f.key.endsWith('_keyword')) {
        const baseKey = f.key.slice(0, -'_keyword'.length);
        if (keySet.has(baseKey)) return false;
      }

      // Drop non-custom _fields_flattened dot-path fields. These mirror
      // top-level _keyword aliases but are not populated in search results,
      // producing blank columns. Custom _fields_flattened paths are kept
      // because they are the only access path for those custom field values.
      // e.g. tas_program_instance_fields_flattened.* → excluded
      //      tas_program_instance_custom_fields_flattened.* → kept
      if (f.key.includes('.')) {
        const parent = f.key.split('.')[0] ?? '';
        if (/^(tas_)?[a-z_]+_fields_flattened$/i.test(parent) && !parent.includes('custom')) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return fields;
}

// Backward-compatible aliases for the previous /fields API types (used by tests)
export type FieldsApiField = EnhancedSearchField;
export type FieldsApiResponse = EnhancedFieldsResponse;
